import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import HowToRegIcon from '@mui/icons-material/HowToRegOutlined';
import LockIcon from '@mui/icons-material/LockOutlined';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLongOutlined';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { useIntl } from 'react-intl';
import { useSearchParams } from 'react-router-dom';
import {
  getDevEntraAccessTokenServerSnapshot,
  getDevEntraAccessTokenSnapshot,
  subscribeDevEntraAccessToken,
} from '../../infrastructure/api/dev-entra-token-storage';
import {
  type GenerateApiKeyResponse,
  type InvitationFlowDisplayContext,
  getStoredEntraAccessToken,
  regenerateApiKeySecretForCpa,
  revokeApiKeysByIdsForCpa,
} from '../../infrastructure/api/invitation.api.repository';
import { KeyRow, LanguageDropdown, VerificationCodeInput } from '../components';
import { useGenerateApiKey } from '../hooks/useGenerateApiKey';
import { useInvitation } from '../hooks/useInvitation';
import { useSendVerificationCode } from '../hooks/useSendVerificationCode';
import { useValidateVerificationCode } from '../hooks/useValidateVerificationCode';

const PAGE_LOGO_SX = {
  position: 'absolute' as const,
  right: 0,
  objectFit: 'contain' as const,
  zIndex: 0,
  userSelect: 'none',
};

const MAIN_CONTENT_SX = { position: 'relative' as const, zIndex: 1 };

const CONTENT_MAX_WIDTH = 520;
const CEGID_COLOR = '#0046fe';
const CARD_PADDING_SX = { pt: 3, px: 4, pb: 4 };
const CARD_BOX_SHADOW =
  '0 4px 5px 0 rgba(0, 44, 82, 0.08), 0 1px 10px 0 rgba(0, 44, 82, 0.12), 0 2px 4px -1px rgba(0, 44, 82, 0.12)';
const HEADER_MB = 2.5;
const SUBTITLE_MB = 2;
const INFO_BLOCK_BG = '#f7f8f9';
const INFO_BLOCK_PADDING_SX = { py: 2, px: 3 };
const FONT_POPPINS = "'Poppins', sans-serif";
const FONT_ROBOTO = "'Roboto', sans-serif";
const PRIMARY_BUTTON_SX = {
  bgcolor: CEGID_COLOR,
  '&:hover': { bgcolor: '#0039cc' },
};
const SECURE_CHIP_SX = {
  bgcolor: '#e3ffd0',
  border: 'none',
};

// Fond bleu très clair (blue 50), texte bleu foncé – même principe que le second cadre en bleu
const INTRO_BOX_SX = {
  borderRadius: 2,
  bgcolor: '#e3f2fd',
  color: 'primary.dark',
  p: 2,
  mb: 3,
  textAlign: 'left' as const,
};

// Fond vert très clair, proche de l’Alert success du Snackbar (green 50)
const API_KEY_EXPLANATION_BOX_SX = {
  borderRadius: 2,
  bgcolor: '#e8f5e9',
  color: 'success.dark',
  p: 2,
  mt: 2,
};

function isHttpsImageUrl(url: string | null | undefined): url is string {
  if (url == null || url === '') return false;
  try {
    return new URL(url).protocol === 'https:';
  } catch {
    return false;
  }
}

function formatBusinessOwnerDisplay(ctx: InvitationFlowDisplayContext): string {
  const first = ctx.business_owner_first_name?.trim();
  const last = ctx.business_owner_last_name.trim();
  return first ? `${first} ${last}` : last;
}

function formatCollaboratorDisplay(ctx: InvitationFlowDisplayContext): string {
  const first = ctx.collaborator_first_name?.trim();
  const last = ctx.collaborator_last_name.trim();
  return first ? `${first} ${last}` : last;
}

export function InvitationPage() {
  const intl = useIntl();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [copiedKind, setCopiedKind] = useState<'id' | 'secret' | null>(null);
  const [regenerateDialog, setRegenerateDialog] =
    useState<GenerateApiKeyResponse | null>(null);
  const [regenerateDialogCopyKind, setRegenerateDialogCopyKind] = useState<
    'id' | 'secret' | null
  >(null);
  const [cpaActionLoading, setCpaActionLoading] = useState(false);
  const [cpaActionSnackbar, setCpaActionSnackbar] = useState<string | null>(
    null,
  );
  const [selectedIntegrationIds, setSelectedIntegrationIds] = useState<
    Set<string>
  >(() => new Set());
  /** Local overrides after revoke / regenerate (static invitation error payload is not refetched). */
  const [integrationStatusOverride, setIntegrationStatusOverride] = useState<
    Map<string, 'active' | 'revoked'>
  >(() => new Map());

  const {
    data: invitation,
    isLoading: isValidating,
    isError: isValidationError,
    error: validationError,
    refetch,
  } = useInvitation(token);

  const {
    generateApiKey,
    public_api_integration_id,
    api_key_secret,
    isLoading: isGenerating,
    isError: isGenerateError,
    error: generateError,
    reset: resetGenerate,
  } = useGenerateApiKey();

  const { sendCode, sendCodeAsync, isSending } = useSendVerificationCode();
  const [resendSuccessSnackbarOpen, setResendSuccessSnackbarOpen] =
    useState(false);
  const [resendCooldownRemaining, setResendCooldownRemaining] = useState(0);
  const {
    validateCode,
    isValidating: isValidatingCode,
    isError: isValidateCodeError,
    reset: resetValidateCode,
    data: validateCodeData,
  } = useValidateVerificationCode();

  const devEntraAccessToken = useSyncExternalStore(
    subscribeDevEntraAccessToken,
    getDevEntraAccessTokenSnapshot,
    getDevEntraAccessTokenServerSnapshot,
  );

  const hasGeneratedKeys = Boolean(public_api_integration_id && api_key_secret);
  const codeValidated = validateCodeData?.valid === true;

  const sendCodeSentRef = useRef(false);
  const initialCooldownStartedRef = useRef(false);

  useEffect(() => {
    setSelectedIntegrationIds(new Set());
    setIntegrationStatusOverride(new Map());
  }, [token]);

  useEffect(() => {
    if (!token || !invitation || sendCodeSentRef.current) return;
    sendCodeSentRef.current = true;
    sendCode(token, intl.locale as 'fr' | 'en');
  }, [token, invitation, sendCode, intl.locale]);

  // Démarrer le cooldown de 60 s dès l’affichage du formulaire (premier chargement ou après rafraîchissement)
  useEffect(() => {
    if (!invitation || codeValidated || initialCooldownStartedRef.current)
      return;
    initialCooldownStartedRef.current = true;
    queueMicrotask(() => setResendCooldownRemaining(60));
  }, [invitation, codeValidated]);

  useEffect(() => {
    if (resendCooldownRemaining <= 0) return;
    const timer = setInterval(() => {
      setResendCooldownRemaining((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldownRemaining]);

  const handleCopy = useCallback((text: string, kind: 'id' | 'secret') => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(
      () => {
        setCopiedKind(kind);
        setTimeout(() => setCopiedKind(null), 2000);
      },
      () => undefined,
    );
  }, []);

  const handleGenerateClick = useCallback(() => {
    if (!token) return;
    resetGenerate();
    generateApiKey({ token });
  }, [token, generateApiKey, resetGenerate]);

  const handleValidateCode = useCallback(
    (code: string) => {
      if (!token || code.length !== 6) return;
      resetValidateCode();
      validateCode({ token, code });
    },
    [token, validateCode, resetValidateCode],
  );

  if (!token || token.trim() === '') {
    return (
      <Box sx={{ position: 'relative' }}>
        <Box component="img" src="/mongolfier.png" alt="" sx={PAGE_LOGO_SX} />
        <Box
          sx={{
            p: 3,
            maxWidth: 520,
            mx: 'auto',
            backgroundColor: 'white',
            ...MAIN_CONTENT_SX,
          }}
        >
          <Typography variant="h4" component="h1" gutterBottom>
            {intl.formatMessage({ id: 'invitation.pageTitle' })}
          </Typography>
          <Box sx={INTRO_BOX_SX}>
            <Typography sx={{ color: 'inherit' }}>
              {intl.formatMessage({ id: 'invitation.pageIntro' })}
            </Typography>
          </Box>
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="h6" color="error" gutterBottom>
              {intl.formatMessage({ id: 'invitation.errorNoTokenTitle' })}
            </Typography>
            <Typography color="text.secondary">
              {intl.formatMessage({ id: 'invitation.errorNoTokenBody' })}
            </Typography>
          </Paper>
        </Box>
      </Box>
    );
  }

  if (isValidating) {
    return (
      <Box sx={{ position: 'relative' }}>
        <Box component="img" src="/mongolfier.png" alt="" sx={PAGE_LOGO_SX} />
        <Box
          sx={{
            p: 3,
            maxWidth: 520,
            mx: 'auto',
            backgroundColor: 'white',
            textAlign: 'center',
            ...MAIN_CONTENT_SX,
          }}
        >
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{ textAlign: 'left' }}
          >
            {intl.formatMessage({ id: 'invitation.pageTitle' })}
          </Typography>
          <Box sx={INTRO_BOX_SX}>
            <Typography sx={{ color: 'inherit' }}>
              {intl.formatMessage({ id: 'invitation.pageIntro' })}
            </Typography>
          </Box>
          <CircularProgress />
          <Typography sx={{ mt: 2 }} color="text.secondary">
            {intl.formatMessage({ id: 'invitation.verifyingLink' })}
          </Typography>
        </Box>
      </Box>
    );
  }

  if (isValidationError && validationError) {
    const isTokenError = validationError.kind === 'token';
    const code = isTokenError ? validationError.code : null;
    const expiredDetails = isTokenError
      ? validationError.expiredDetails
      : undefined;
    const revokedDetails = isTokenError
      ? validationError.revokedDetails
      : undefined;

    const invitationRevoked =
      revokedDetails?.variant === 'invitation_revoked'
        ? revokedDetails
        : undefined;
    const hasActiveIntegrationForTitle =
      invitationRevoked != null &&
      (invitationRevoked.integrations.some((i) => i.status === 'active') ||
        invitationRevoked.activeIntegrationCreator != null);

    const title =
      code === 'TOKEN_NOT_FOUND'
        ? intl.formatMessage({ id: 'invitation.errorInvalidLinkTitle' })
        : code === 'TOKEN_EXPIRED'
          ? intl.formatMessage({ id: 'invitation.errorExpiredTitle' })
          : code === 'TOKEN_REVOKED'
            ? hasActiveIntegrationForTitle
              ? intl.formatMessage({
                  id: 'invitation.errorRevokedKeyAlreadyActiveTitle',
                })
              : intl.formatMessage({ id: 'invitation.errorRevokedTitle' })
            : intl.formatMessage({ id: 'invitation.errorConnectionTitle' });

    let message: string;
    if (code === 'TOKEN_NOT_FOUND') {
      message = intl.formatMessage({
        id: 'invitation.errorInvalidLinkBody',
      });
    } else if (code === 'TOKEN_EXPIRED') {
      if (
        expiredDetails?.variant === 'integration_owner' &&
        expiredDetails.owner
      ) {
        message = intl.formatMessage(
          { id: 'invitation.errorExpiredIntegrationOwner' },
          {
            firstName: expiredDetails.owner.firstName,
            lastName: expiredDetails.owner.lastName,
            email: expiredDetails.owner.email,
          },
        );
      } else {
        message = intl.formatMessage({
          id: 'invitation.errorExpiredContactCpa',
        });
      }
    } else if (code === 'TOKEN_REVOKED') {
      if (invitationRevoked) {
        const c = invitationRevoked.activeIntegrationCreator;
        if (c?.firstName && c?.lastName && c?.email) {
          message = intl.formatMessage(
            { id: 'invitation.errorRevokedKeyAlreadyActiveWithCreator' },
            {
              firstName: c.firstName,
              lastName: c.lastName,
              email: c.email,
            },
          );
        } else if (
          invitationRevoked.integrations.some((i) => i.status === 'active')
        ) {
          message = intl.formatMessage({
            id: 'invitation.errorRevokedKeyAlreadyActiveNoCreator',
          });
        } else {
          message = intl.formatMessage({ id: 'invitation.errorRevokedBody' });
        }
      } else {
        message = intl.formatMessage({ id: 'invitation.errorRevokedBody' });
      }
    } else {
      message = validationError.message ?? '';
    }

    const effectiveStatusOf = (row: {
      id: string;
      status: 'active' | 'revoked';
    }): 'active' | 'revoked' =>
      integrationStatusOverride.get(row.id) ?? row.status;

    const canRunCpaActions = devEntraAccessToken !== '';
    const rows = invitationRevoked?.integrations ?? [];
    const activeIntegrationIds = rows
      .filter((r) => effectiveStatusOf(r) === 'active')
      .map((r) => r.id);
    const allActiveSelected =
      activeIntegrationIds.length > 0 &&
      activeIntegrationIds.every((id) => selectedIntegrationIds.has(id));
    const someActiveSelected = activeIntegrationIds.some((id) =>
      selectedIntegrationIds.has(id),
    );
    const selectedList = [...selectedIntegrationIds];
    const singleSelectedRow =
      selectedList.length === 1
        ? rows.find((r) => r.id === selectedList[0])
        : undefined;
    const singleEffectiveStatus = singleSelectedRow
      ? effectiveStatusOf(singleSelectedRow)
      : null;
    const canRunRegen =
      canRunCpaActions &&
      singleSelectedRow != null &&
      (singleEffectiveStatus === 'active' ||
        singleEffectiveStatus === 'revoked');
    const activeSelectedIds = selectedList.filter((id) => {
      const row = rows.find((r) => r.id === id);
      return row != null && effectiveStatusOf(row) === 'active';
    });
    const canRunRevokeIds = canRunCpaActions && activeSelectedIds.length >= 1;
    const needEntraHelp = intl.formatMessage({
      id: 'invitation.revokedActionsNeedEntra',
    });
    const regenSelectHelp = intl.formatMessage({
      id: 'invitation.revokedRegenerateNeedSelection',
    });
    const revokeSelectHelp = intl.formatMessage({
      id: 'invitation.revokedRevokeNeedSelection',
    });

    return (
      <Box sx={{ position: 'relative' }}>
        <Box component="img" src="/mongolfier.png" alt="" sx={PAGE_LOGO_SX} />
        <Box
          sx={{
            p: 3,
            maxWidth: 560,
            backgroundColor: 'white',
            mx: 'auto',
            ...MAIN_CONTENT_SX,
          }}
        >
          <Typography variant="h4" component="h1" gutterBottom>
            {intl.formatMessage({ id: 'invitation.pageTitle' })}
          </Typography>
          <Box sx={INTRO_BOX_SX}>
            <Typography sx={{ color: 'inherit' }}>
              {intl.formatMessage({ id: 'invitation.pageIntro' })}
            </Typography>
          </Box>
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="h6" color="error" gutterBottom>
              {title}
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              {message}
            </Typography>
            {code === 'TOKEN_REVOKED' && invitationRevoked && (
              <>
                {invitationRevoked.integrations.length > 0 && (
                  <>
                    <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                      {intl.formatMessage({
                        id: 'invitation.revokedIntegrationsTitle',
                      })}
                    </Typography>
                    <Table size="small" sx={{ mb: 2 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox" width={48}>
                            {activeIntegrationIds.length > 0 ? (
                              <Checkbox
                                size="small"
                                indeterminate={
                                  someActiveSelected && !allActiveSelected
                                }
                                checked={allActiveSelected}
                                onChange={() => {
                                  setSelectedIntegrationIds((prev) => {
                                    const next = new Set(prev);
                                    if (allActiveSelected) {
                                      activeIntegrationIds.forEach((id) =>
                                        next.delete(id),
                                      );
                                    } else {
                                      activeIntegrationIds.forEach((id) =>
                                        next.add(id),
                                      );
                                    }
                                    return next;
                                  });
                                }}
                                inputProps={{
                                  'aria-label': intl.formatMessage({
                                    id: 'invitation.revokedSelectAllActive',
                                  }),
                                }}
                              />
                            ) : null}
                          </TableCell>
                          <TableCell>
                            {intl.formatMessage({
                              id: 'invitation.revokedIntegrationIdColumn',
                            })}
                          </TableCell>
                          <TableCell align="right" width={120}>
                            {intl.formatMessage({
                              id: 'invitation.revokedIntegrationStatusColumn',
                            })}
                          </TableCell>
                          <TableCell align="right" width={56} />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {invitationRevoked.integrations.map((row) => {
                          const effective = effectiveStatusOf(row);
                          const showCheckbox =
                            effective === 'active' || effective === 'revoked';
                          return (
                            <TableRow key={row.id}>
                              <TableCell padding="checkbox">
                                {showCheckbox ? (
                                  <Checkbox
                                    size="small"
                                    checked={selectedIntegrationIds.has(row.id)}
                                    onChange={() => {
                                      setSelectedIntegrationIds((prev) => {
                                        const next = new Set(prev);
                                        if (next.has(row.id)) {
                                          next.delete(row.id);
                                        } else {
                                          next.add(row.id);
                                        }
                                        return next;
                                      });
                                    }}
                                    inputProps={{
                                      'aria-label': intl.formatMessage(
                                        {
                                          id: 'invitation.revokedSelectRow',
                                        },
                                        { id: row.id },
                                      ),
                                    }}
                                  />
                                ) : null}
                              </TableCell>
                              <TableCell>
                                <Typography
                                  component="code"
                                  variant="body2"
                                  sx={{ wordBreak: 'break-all' }}
                                >
                                  {row.id}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Chip
                                  size="small"
                                  label={
                                    effective === 'active'
                                      ? intl.formatMessage({
                                          id: 'invitation.revokedIntegrationStatusActive',
                                        })
                                      : intl.formatMessage({
                                          id: 'invitation.revokedIntegrationStatusRevoked',
                                        })
                                  }
                                  color={
                                    effective === 'active'
                                      ? 'success'
                                      : 'default'
                                  }
                                />
                              </TableCell>
                              <TableCell align="right">
                                <Tooltip
                                  title={intl.formatMessage({
                                    id: 'invitation.revokedCopyId',
                                  })}
                                >
                                  <IconButton
                                    size="small"
                                    aria-label={intl.formatMessage({
                                      id: 'invitation.revokedCopyId',
                                    })}
                                    onClick={() => {
                                      void navigator.clipboard.writeText(
                                        row.id,
                                      );
                                    }}
                                  >
                                    <ContentCopyIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </>
                )}
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  sx={{ mb: 1 }}
                >
                  <Tooltip
                    title={
                      !canRunCpaActions
                        ? needEntraHelp
                        : !canRunRegen
                          ? regenSelectHelp
                          : ''
                    }
                  >
                    <span>
                      <Button
                        variant="contained"
                        sx={PRIMARY_BUTTON_SX}
                        disabled={!canRunRegen || cpaActionLoading}
                        onClick={() => {
                          const bearer = getStoredEntraAccessToken();
                          if (!bearer) return;
                          const [onlySelected] = [...selectedIntegrationIds];
                          if (onlySelected === undefined) return;
                          setCpaActionLoading(true);
                          void (async () => {
                            try {
                              const res = await regenerateApiKeySecretForCpa({
                                externalCompanyId:
                                  invitationRevoked.externalCompanyId,
                                partnerApplicationId:
                                  invitationRevoked.partnerApplicationId,
                                authorizationBearer: bearer,
                                publicApiIntegrationId: onlySelected,
                              });
                              setRegenerateDialog(res);
                              setIntegrationStatusOverride((prev) => {
                                const next = new Map(prev);
                                if (
                                  res.public_api_integration_id === onlySelected
                                ) {
                                  next.set(onlySelected, 'active');
                                } else {
                                  next.set(onlySelected, 'revoked');
                                }
                                return next;
                              });
                              setCpaActionSnackbar(
                                intl.formatMessage({
                                  id: 'invitation.revokedRegenerateSuccess',
                                }),
                              );
                            } catch {
                              setCpaActionSnackbar(
                                intl.formatMessage({
                                  id: 'invitation.revokedActionError',
                                }),
                              );
                            } finally {
                              setCpaActionLoading(false);
                            }
                          })();
                        }}
                      >
                        {cpaActionLoading
                          ? intl.formatMessage({
                              id: 'invitation.revokedRenewInProgress',
                            })
                          : intl.formatMessage({
                              id: 'invitation.revokedRenewSecret',
                            })}
                      </Button>
                    </span>
                  </Tooltip>
                  <Tooltip
                    title={
                      !canRunCpaActions
                        ? needEntraHelp
                        : !canRunRevokeIds
                          ? revokeSelectHelp
                          : ''
                    }
                  >
                    <span>
                      <Button
                        variant="outlined"
                        color="error"
                        disabled={!canRunRevokeIds || cpaActionLoading}
                        onClick={() => {
                          const bearer = getStoredEntraAccessToken();
                          if (!bearer) return;
                          if (activeSelectedIds.length === 0) return;
                          setCpaActionLoading(true);
                          void (async () => {
                            try {
                              const outcome = await revokeApiKeysByIdsForCpa(
                                {
                                  externalCompanyId:
                                    invitationRevoked.externalCompanyId,
                                  partnerApplicationId:
                                    invitationRevoked.partnerApplicationId,
                                  publicApiIntegrationIds: activeSelectedIds,
                                  revocationReason: 'OrganizationRequest',
                                },
                                { authorizationBearer: bearer },
                              );
                              setIntegrationStatusOverride((prev) => {
                                const next = new Map(prev);
                                for (const id of activeSelectedIds) {
                                  next.set(id, 'revoked');
                                }
                                return next;
                              });
                              setCpaActionSnackbar(
                                intl.formatMessage(
                                  {
                                    id: 'invitation.revokedBulkRevokeSuccess',
                                  },
                                  {
                                    revoked: String(outcome.revokedCount),
                                    already: String(
                                      outcome.alreadyRevokedCount,
                                    ),
                                  },
                                ),
                              );
                              setSelectedIntegrationIds(new Set());
                            } catch {
                              setCpaActionSnackbar(
                                intl.formatMessage({
                                  id: 'invitation.revokedActionError',
                                }),
                              );
                            } finally {
                              setCpaActionLoading(false);
                            }
                          })();
                        }}
                      >
                        {intl.formatMessage({
                          id: 'invitation.revokedRevokeKey',
                        })}
                      </Button>
                    </span>
                  </Tooltip>
                </Stack>
              </>
            )}
            {validationError.kind === 'network' && (
              <Button variant="outlined" onClick={() => refetch()}>
                {intl.formatMessage({ id: 'invitation.retryButton' })}
              </Button>
            )}
          </Paper>
        </Box>
        <Dialog
          open={regenerateDialog != null}
          onClose={() => setRegenerateDialog(null)}
          fullWidth
          maxWidth="sm"
        >
          {regenerateDialog != null && (
            <>
              <DialogTitle>
                {intl.formatMessage({ id: 'invitation.revokedNewSecretTitle' })}
              </DialogTitle>
              <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <KeyRow
                    label={intl.formatMessage({
                      id: 'invitation.publicApiIntegrationIdLabel',
                    })}
                    value={regenerateDialog.public_api_integration_id}
                    copyAriaLabel={intl.formatMessage({
                      id: 'invitation.copyPublicApiIntegrationId',
                    })}
                    showLabel={intl.formatMessage({
                      id: 'invitation.showValue',
                    })}
                    hideLabel={intl.formatMessage({
                      id: 'invitation.hideValue',
                    })}
                    copyKind="id"
                    onCopy={(text, kind) => {
                      void navigator.clipboard.writeText(text);
                      setRegenerateDialogCopyKind(kind);
                      setTimeout(() => setRegenerateDialogCopyKind(null), 2000);
                    }}
                    copied={regenerateDialogCopyKind === 'id'}
                  />
                  <KeyRow
                    label={intl.formatMessage({
                      id: 'invitation.apiIntegrationSecretLabel',
                    })}
                    value={regenerateDialog.api_key_secret}
                    copyAriaLabel={intl.formatMessage({
                      id: 'invitation.copyApiIntegrationSecret',
                    })}
                    showLabel={intl.formatMessage({
                      id: 'invitation.showValue',
                    })}
                    hideLabel={intl.formatMessage({
                      id: 'invitation.hideValue',
                    })}
                    copyKind="secret"
                    onCopy={(text, kind) => {
                      void navigator.clipboard.writeText(text);
                      setRegenerateDialogCopyKind(kind);
                      setTimeout(() => setRegenerateDialogCopyKind(null), 2000);
                    }}
                    copied={regenerateDialogCopyKind === 'secret'}
                  />
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setRegenerateDialog(null)}>
                  {intl.formatMessage({ id: 'invitation.revokedDialogClose' })}
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>
        <Snackbar
          open={cpaActionSnackbar != null}
          autoHideDuration={5000}
          onClose={() => setCpaActionSnackbar(null)}
          message={cpaActionSnackbar ?? ''}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        />
      </Box>
    );
  }

  if (!invitation) {
    return null;
  }

  const label = invitation.integrationServiceLabel;
  const msg = (id: string, values?: { integrationServiceLabel: string }) =>
    intl.formatMessage({ id }, values ?? { integrationServiceLabel: label });

  return (
    <Box sx={{ position: 'relative' }}>
      <Box component="img" src="/mongolfier.png" alt="" sx={PAGE_LOGO_SX} />
      <Box
        sx={{
          p: 3,
          maxWidth: CONTENT_MAX_WIDTH,
          backgroundColor: 'white',
          mx: 'auto',
          ...MAIN_CONTENT_SX,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: HEADER_MB,
          }}
        >
          <Typography
            component="span"
            sx={{
              fontFamily: FONT_POPPINS,
              color: CEGID_COLOR,
              fontWeight: 600,
            }}
          >
            cegid
          </Typography>
          <Chip
            label={msg('invitation.secureChip')}
            size="small"
            sx={SECURE_CHIP_SX}
          />
        </Box>

        <Paper
          elevation={0}
          sx={{ ...CARD_PADDING_SX, boxShadow: CARD_BOX_SHADOW }}
        >
          {codeValidated ? (
            <Box
              sx={{
                mb: 2,
                p: 2,
                borderRadius: 2,
                bgcolor: '#fafbfc',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              {isHttpsImageUrl(invitation.display_context.partner_logo_url) ? (
                <Box
                  component="img"
                  src={invitation.display_context.partner_logo_url}
                  alt=""
                  sx={{
                    display: 'block',
                    maxHeight: 48,
                    maxWidth: '100%',
                    objectFit: 'contain',
                    mx: 'auto',
                    mb: 1.5,
                  }}
                />
              ) : null}
              <Typography
                variant="subtitle1"
                sx={{
                  fontFamily: FONT_POPPINS,
                  fontWeight: 600,
                  textAlign: 'center',
                  color: '#002C52',
                }}
              >
                {invitation.display_context.partner_name}
              </Typography>
              {invitation.display_context.partner_description.trim() !== '' ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mt: 0.5,
                    textAlign: 'center',
                    whiteSpace: 'pre-wrap',
                    fontFamily: FONT_ROBOTO,
                  }}
                >
                  {invitation.display_context.partner_description}
                </Typography>
              ) : null}
              <Divider sx={{ my: 2 }} />
              <Typography
                variant="body2"
                sx={{
                  fontFamily: FONT_ROBOTO,
                  textAlign: 'left',
                  color: 'text.primary',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {intl.formatMessage(
                  { id: 'invitation.contextValidatedSummary' },
                  {
                    businessOwner: formatBusinessOwnerDisplay(
                      invitation.display_context,
                    ),
                    collaborator: formatCollaboratorDisplay(
                      invitation.display_context,
                    ),
                    inviterCompanyName:
                      invitation.display_context.inviter_company_name,
                  },
                )}
              </Typography>
            </Box>
          ) : null}

          <Typography
            component="h1"
            sx={{
              fontSize: 14,
              fontFamily: FONT_ROBOTO,
              fontWeight: 400,
              textAlign: 'center',
              color: '#002C52',
              mb: SUBTITLE_MB,
            }}
          >
            {msg('invitation.cardSubtitle')}
          </Typography>

          {!codeValidated ? (
            <>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: FONT_ROBOTO,
                  color: 'text.secondary',
                  textAlign: 'center',
                  mb: 2,
                }}
              >
                {intl.formatMessage(
                  { id: 'invitation.codeExplanation' },
                  {
                    maskedEmail:
                      invitation.maskedCustomerEmail ??
                      intl.formatMessage({
                        id: 'invitation.codeEmailFallback',
                      }),
                  },
                )}
              </Typography>
              <VerificationCodeInput
                onValidate={handleValidateCode}
                isValidating={isValidatingCode}
                error={
                  isValidateCodeError ||
                  (validateCodeData != null && !validateCodeData.valid)
                }
                errorReason={
                  validateCodeData != null && !validateCodeData.valid
                    ? validateCodeData.reason
                    : undefined
                }
                onResend={
                  token
                    ? () => {
                        resetValidateCode();
                        void sendCodeAsync(
                          token,
                          intl.locale as 'fr' | 'en',
                        ).then(() => {
                          setResendSuccessSnackbarOpen(true);
                          setResendCooldownRemaining(60);
                        });
                      }
                    : undefined
                }
                resendCooldownSeconds={resendCooldownRemaining}
                isSending={isSending}
              />
            </>
          ) : (
            <>
              <Box
                sx={{
                  backgroundColor: INFO_BLOCK_BG,
                  ...INFO_BLOCK_PADDING_SX,
                  mb: 2,
                }}
              >
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <ReceiptLongIcon sx={{ color: 'text.secondary', mt: 0.25 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontFamily: FONT_POPPINS, fontWeight: 600 }}
                    >
                      {msg('invitation.infoInvoicingTitle')}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: FONT_ROBOTO, color: 'text.secondary' }}
                    >
                      {msg('invitation.infoInvoicingText')}
                    </Typography>
                  </Box>
                </Box>
                <Divider
                  sx={{
                    borderColor: '#001D3633',
                    my: 1.5,
                    ml: 4.5,
                  }}
                />
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <LockIcon sx={{ color: 'text.secondary', mt: 0.25 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontFamily: FONT_POPPINS, fontWeight: 600 }}
                    >
                      {msg('invitation.infoSecureTitle')}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: FONT_ROBOTO, color: 'text.secondary' }}
                    >
                      {msg('invitation.infoSecureText')}
                    </Typography>
                  </Box>
                </Box>
                <Divider
                  sx={{
                    borderColor: '#001D3633',
                    my: 1.5,
                    ml: 4.5,
                  }}
                />
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <HowToRegIcon sx={{ color: 'text.secondary', mt: 0.25 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontFamily: FONT_POPPINS, fontWeight: 600 }}
                    >
                      {msg('invitation.infoControlTitle')}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: FONT_ROBOTO, color: 'text.secondary' }}
                    >
                      {msg('invitation.infoControlText')}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Paper elevation={0} sx={{ p: 2, mb: 2 }}>
                {!hasGeneratedKeys ? (
                  <>
                    <Button
                      variant="contained"
                      onClick={handleGenerateClick}
                      disabled={isGenerating}
                      sx={PRIMARY_BUTTON_SX}
                      fullWidth
                    >
                      {isGenerating ? (
                        <>
                          <CircularProgress
                            size={20}
                            sx={{ mr: 1 }}
                            color="inherit"
                          />
                          {intl.formatMessage({ id: 'invitation.generating' })}
                        </>
                      ) : (
                        intl.formatMessage({ id: 'invitation.generateButton' })
                      )}
                    </Button>
                    {isGenerateError && generateError && (
                      <Alert severity="error" sx={{ mt: 2 }}>
                        {generateError.kind === 'sessionExpired'
                          ? intl.formatMessage({
                              id: 'invitation.generateErrorSessionExpired',
                            })
                          : generateError.kind === 'token'
                            ? intl.formatMessage({
                                id: 'invitation.generateErrorInvalidLink',
                              })
                            : generateError.message}
                      </Alert>
                    )}
                  </>
                ) : (
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                  >
                    <KeyRow
                      label={intl.formatMessage({
                        id: 'invitation.publicApiIntegrationIdLabel',
                      })}
                      value={public_api_integration_id ?? ''}
                      copyAriaLabel={intl.formatMessage({
                        id: 'invitation.copyPublicApiIntegrationId',
                      })}
                      showLabel={intl.formatMessage({
                        id: 'invitation.showValue',
                      })}
                      hideLabel={intl.formatMessage({
                        id: 'invitation.hideValue',
                      })}
                      copyKind="id"
                      onCopy={handleCopy}
                      copied={copiedKind === 'id'}
                    />
                    <KeyRow
                      label={intl.formatMessage({
                        id: 'invitation.apiIntegrationSecretLabel',
                      })}
                      value={api_key_secret ?? ''}
                      copyAriaLabel={intl.formatMessage({
                        id: 'invitation.copyApiIntegrationSecret',
                      })}
                      showLabel={intl.formatMessage({
                        id: 'invitation.showValue',
                      })}
                      hideLabel={intl.formatMessage({
                        id: 'invitation.hideValue',
                      })}
                      copyKind="secret"
                      onCopy={handleCopy}
                      copied={copiedKind === 'secret'}
                    />
                  </Box>
                )}
              </Paper>

              {hasGeneratedKeys && (
                <Box sx={API_KEY_EXPLANATION_BOX_SX}>
                  <Typography variant="body2" sx={{ color: 'inherit' }}>
                    {msg('invitation.explanatoryText')}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Paper>

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <LanguageDropdown />
        </Box>

        <Snackbar
          open={copiedKind !== null}
          autoHideDuration={2500}
          onClose={() => setCopiedKind(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity="success" onClose={() => setCopiedKind(null)}>
            {copiedKind === 'id'
              ? intl.formatMessage({ id: 'invitation.copiedIdSnackbar' })
              : copiedKind === 'secret'
                ? intl.formatMessage({ id: 'invitation.copiedSecretSnackbar' })
                : ''}
          </Alert>
        </Snackbar>
        <Snackbar
          open={resendSuccessSnackbarOpen}
          autoHideDuration={3500}
          onClose={() => setResendSuccessSnackbarOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            severity="success"
            onClose={() => setResendSuccessSnackbarOpen(false)}
          >
            {intl.formatMessage({ id: 'invitation.codeResentSnackbar' })}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
}
